// "Tasks" box on a record page (order, product, sample, work order): the
// tasks linked to this record, plus a button that opens a new task already
// linked to it. Only the caller's own org's tasks ever appear.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { formatPlainDate, parsePlainDate } from '../../lib/datetime'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import { listTasks, staffTaskKeys, type TaskLink } from './api'
import { assigneeNames, taskDetailRoute, type TaskShell } from './lib'
import { TaskFormDialog } from './TaskFormDialog'

export function LinkedTasksPanel({
  shell,
  link,
  className,
}: {
  shell: TaskShell
  link: TaskLink
  className?: string
}) {
  const t = useT(staffTasksI18n)
  const [open, setOpen] = useState(false)
  const params = { scope: 'all' as const, linked_kind: link.kind, linked_id: link.id, limit: 50 }
  const tasks = useQuery({
    queryKey: staffTaskKeys.list(params),
    queryFn: () => listTasks(params),
    retry: false,
  })
  // A shell or role without the task capability gets a 403: show nothing.
  if (tasks.isError) return null
  const items = tasks.data?.items ?? []

  return (
    <section
      aria-labelledby={`linked-tasks-${link.id}`}
      data-testid="linked-tasks-panel"
      className={cn(
        'rounded-[var(--surface-lifted-radius)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] p-4 shadow-[var(--surface-lifted-shadow)]',
        className,
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 id={`linked-tasks-${link.id}`} className="m-0 text-sm font-semibold">
          {t.linked.title}
        </h2>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="linked-task-add">
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t.linked.add}
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="m-0 text-sm text-[var(--color-text-muted)]">
          {tasks.isLoading ? '…' : t.linked.empty}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {items.map((task) => {
            const due = task.due_date ? parsePlainDate(task.due_date) : null
            return (
              <li key={task.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                <Link
                  {...taskDetailRoute(shell, task.id)}
                  className={cn(
                    'font-medium hover:underline',
                    task.status === 'done' && 'text-[var(--color-text-muted)] line-through',
                  )}
                >
                  {task.title}
                </Link>
                {task.priority === 'urgent' && task.status !== 'done' && (
                  <Badge variant="destructive">{t.priority.urgent}</Badge>
                )}
                <span className="text-xs text-[var(--color-text-muted)]">
                  {t.status[task.status]}
                  {task.assignees.length > 0 &&
                    ` · ${assigneeNames(task.assignees, t.structure.more)}`}
                  {due && (
                    <span className={cn(task.is_overdue && 'font-medium text-[var(--color-danger)]')}>
                      {' · '}
                      {formatPlainDate(due)}
                    </span>
                  )}
                </span>
              </li>
            )
          })}
        </ul>
      )}
      <TaskFormDialog open={open} onOpenChange={setOpen} link={link} />
    </section>
  )
}
